/**
 * Pure (side-effect-free) логика видимости модулей.
 * office_manager + payer → FIXED_ROLES (2026-06-15).
 *
 * Извлечена из ModuleContext.jsx 2026-05-11 (followup PR #631), чтобы:
 *  - можно было покрыть unit-тестами без React/vitest setup для контекста;
 *  - регрессия MUST_HAVE_CORE → extra_modules не вернулась в третий раз
 *    (см. memory/feedback_must_have_core_inbox.md).
 *
 * Источник истины ролей — backend/app/models/user.py:UserRole.
 * Источник истины каталога — backend/app/services/module_seed.py:MODULES.
 *
 * НЕ импортирует ничего из проекта (React, api/client, etc) — pure function на
 * входе (key, user, modules) → boolean. Это позволяет тестировать комбинации
 * ролей и custom extra_modules без рендеринга провайдера.
 */

// Order of checks:
//   1. Admin / platform admin → always visible.
//   2. Built-in role + module in MUST_HAVE_CORE (`inbox`, `cabinet`) →
//      always visible, EVEN IF admin set per-user `extra_modules` that omits
//      these. Inbox хостит task tray Workflow Engine MVP (PR-H, кэш v12) —
//      без него сотрудник не получит свои задачи. Cabinet — собственный
//      профиль. Custom admin-defined роли (НЕ в FIXED_ROLES) этот шаг НЕ
//      касается. Инцидент 2026-05-11 (Дынсыма Беликтуевна, role=assistant).
//   3. `extra_modules` семантика (изменена 2026-05-15):
//        - built-in fixed-роль: ДОПОЛНЯЕТ role-default — если модуль в
//          extras, даём доступ безусловно; если нет — продолжаем к
//          WHITELIST_CORE / каталогу. Это значит, что admin не «теряет»
//          role-default при добавлении нового модуля в extras (раньше
//          extras работал как whitelist override и обрезал базу).
//        - custom (admin-defined) роль: extras = whitelist (без изменений).
//          Завхоз продолжает работать ровно по выбранному admin'ом списку.
//   4. Default WHITELIST_CORE для built-in ролей. Custom-роли с
//      пустым extra_modules падают в catalog.
//   5. Catalog (`/admin/modules/`) lookup. Built-in: null/empty allowed_roles
//      = open to all (legacy). Custom-роли БЕЗ прототипа — строгий explicit
//      match.
//   Роль-прототип (ADR-108, #5667) проходит шаги 2, 4 и 5 как встроенная роль,
//   которую она объявила: «ходит как рекрутер» обязано значить одно и то же на
//   двери API и в меню. Шаг 3 она НЕ меняет — см. комментарий у judgedAsFixed.

export const ROLE_ALIASES = {
    dept_head: 'head',
    head: 'head',
    // cmo (директор по маркетингу) — тот же тир, что dept_head (полный клон
    // прав, решение владельца 2026-07-22). Алиас на 'head' даёт роли паритет
    // ВИДИМОСТИ модулей: dept_head видит модули руководителя (dept-dashboard,
    // myteam, team-payments и т.п.) именно через этот alias, т.к. их
    // allowed_roles перечисляют legacy-«head», а не «dept_head». Без алиаса cmo
    // не увидела бы свой же лендинг dept-dashboard (HomeRedirect).
    cmo: 'head',
    // 2026-06-27: `recruiter` БОЛЬШЕ НЕ алиасится на `hr`. Раньше рекрутёр
    // наследовал весь HR-набор меню (Сотрудники, Оборудование, Ключи, разборы
    // онлайн-менеджеров и т.д.) — хотя backend require_role("hr") его НЕ
    // пропускает (app/middleware/auth.py:ROLE_ALIASES без recruiter), так что
    // HR-страницы открывались пустыми/403. Теперь рекрутёр видит только
    // recruiting-модули, где он указан в allowed_roles ЯВНО (см. module_seed.py:
    // candidates/vacancies/requisitions/shortlist/offers/tests/job-profiles/
    // process/analytics/resume-scraper/talent-pool/hh-*/signing/personnel-file)
    // + личный кабинет (WHITELIST_CORE).
    hr_manager: 'hr',
    it_admin: 'it',
};

// Источник истины — backend/app/models/user.py:UserRole.
// `custom` остаётся НЕ-fixed: управляется только через extra_modules
// (admin-defined "Завхоз" и т.п.), без allowed_roles в каталоге.
export const FIXED_ROLES = new Set([
    'admin', 'hr', 'recruiter', 'head', 'dept_head', 'deputy_head', 'employee',
    'it', 'owner', 'finance', 'online_manager', 'office_manager', 'payer', 'senior_online_manager',
    'vip_manager',  // 2026-07-31: менеджер по работе с VIP-клиентами (права зеркалят online_manager), иначе пустой сайдбар
    'partner_manager', 'head_of_partners', 'deputy_head_of_partners', 'okk_specialist', 'cmo', 'methodologist',
    'business_assistant', 'assistant',  // PR-A 2026-05-11: rename + legacy alias
    'consultant',  // 2026-05-30: внешняя роль (права business_assistant), иначе пустой сайдбар
    'marketing_contractor',  // 2026-06-29: подрядчик маркетинга (кабинет самообслуживания), иначе пустой сайдбар
    'ceo',
    // Legacy-aliases на стороне фронта/middleware (см. ROLE_ALIASES выше).
    'hr_manager', 'it_admin',
]);

// MUST_HAVE_CORE — operationally non-removable модули для built-in ролей.
// Расширять только для нового non-removable surface (новый Personal hub,
// новый task tray) — НЕ для всего WHITELIST_CORE (wiki/training/rewards
// остаются overridable через extra_modules для подрядчиков и т.п.).
// 'me/payslip' — личный расчётный лист «Моя зарплата»: каждый сотрудник обязан
// видеть свою зарплату (scope по себе на бэкенде). Non-removable personal surface.
export const MUST_HAVE_CORE = ['inbox', 'cabinet', 'me/payslip'];

// CONSULTANT_WHITELIST — внешняя роль «Консультант» (2026-07-04) сужена до
// «бизнес-процессы + база знаний». Строгий whitelist меню: только конструктор
// процессов (Workflow), Wiki, и личные self-поверхности (inbox/cabinet/support/
// Моё развитие). Без этой ветки consultant как fixed-роль видел бы ВСЕ «открытые»
// модули каталога (шаг 5 ниже) — регресс приватности. Бэкенд-энфорсмент —
// ROLE_PERMISSIONS["consultant"] (без candidates/vacancies/payroll/personnel/
// партнёрки). Роль is_external → без me/payslip (нет зарплаты).
export const CONSULTANT_WHITELIST = [
    'dashboard', 'inbox', 'cabinet', 'support',
    'wiki',                                       // база знаний
    'workflows', 'workflow', 'workflow-history',  // бизнес-процессы (Workflow Studio)
    'growth-me', 'growth-kb',                     // «Моё развитие» + мои артефакты (self)
];

// Подрядчик — внешняя self-service роль, а не урезанный сотрудник.
// Общий MUST_HAVE/WHITELIST_CORE содержит employee-only поверхности
// (me/payslip, expenses), поэтому для подрядчика действует явный список.
export const CONTRACTOR_WHITELIST = [
    'contractor-cabinet', 'inbox', 'cabinet', 'wiki', 'calendar', 'support',
];

// WHITELIST_CORE — default «employee package» для built-in ролей с пустым
// extra_modules. Admin может отрезать любой из этих модулей через
// extra_modules (кроме MUST_HAVE_CORE).
export const WHITELIST_CORE = [
    'inbox', 'cabinet', 'wiki', 'profile',
    'dialogue-trainer', 'expenses', 'rewards',
];

// TRAINING_WHITELIST — ЖЁСТКИЙ whitelist ролей, видящих модуль «Обучение и
// развитие» (moduleKey 'training'). Owner decision 2026-07-19 (Refs #1272):
// модуль ограничен руководством + methodologist + hr + senior_online_manager.
// Обычный online_manager и все прочие роли модуль НЕ видят. Жёсткий — extra_modules
// и каталог НЕ переопределяют (см. hard-gate в isModuleVisible). `head` — алиас
// dept_head; `hr_manager`→`hr` проходит через normalizedRole.
export const TRAINING_WHITELIST = new Set([
    'admin', 'owner', 'ceo', 'dept_head', 'head', 'deputy_head',
    // cmo — руководитель отдела маркетинга, тот же тир, что dept_head
    // (ROLE_PERMISSIONS["cmo"] — его полный клон). При вводе whitelist роль
    // пропустили: она заведена отдельной миграцией assign_cmo_sagalakova
    // (2026-06-22), а список собирали по «классическим» именам ролей.
    'cmo',
    'methodologist', 'hr', 'senior_online_manager',
]);

/**
 * @param {string} key — module key (e.g. 'inbox', '' or '/' → 'dashboard')
 * @param {{role?: string, is_platform_admin?: boolean, extra_modules?: string[],
 *          extraModules?: string[], allowed_modules?: string[],
 *          position_roles?: string[], prototype_roles?: string[]} |
 *          null | undefined} user
 * @param {Array<{key: string, allowed_roles?: string[] | null,
 *                is_enabled?: boolean}>} modules — каталог из /admin/modules/
 * @returns {boolean}
 */
// Whitelist модулей, доступных уволенному сотруднику (status='terminated' на
// linked Employee). Industry pattern: BambooHR self-service window — после
// увольнения сотрудник продолжает логиниться, но видит только: личный
// кабинет (свои данные) + поддержка (связаться с HR). Личное дело
// (personnel-file) сюда НЕ входит — это HR/manager surface, который для
// «себя» избыточен (показывает оценки, заметки руководителя, financials).
// Сам уволенный пользуется cabinet — там его собственные данные read-only.
// `offboarding-handover` — выделенный moduleKey route'а `/erp/offboarding/handover`,
// чтобы terminated-юзер мог открыть страницу описания должности и заполнить её.
export const TERMINATED_WHITELIST = ['cabinet', 'support', 'offboarding-handover'];


// Роли, которые НЕЛЬЗЯ объявить прототипом. Дословная копия
// `PRIVILEGED_ROLES_NOT_ASSIGNABLE_BY_HR` (backend/app/models/user.py) —
// источник истины там, отбор делает сервер (`_usable_base_role`). Здесь копия
// стоит вторым рубежом: подменённый ответ `/auth/me` не должен рисовать
// человеку меню администратора. Разойдётся копия — разойдётся в безопасную
// сторону: сервер такой код и не пришлёт.
export const PROTOTYPE_FORBIDDEN_ROLES = new Set([
    'admin', 'owner', 'ceo', 'cmo', 'finance', 'it', 'hr',
]);

/**
 * Коды ролей-прототипов человека (`custom_roles.base_role`, ADR-108).
 *
 * Прототип — объявление «эта роль ходит как рекрутер». Двери API он открывает
 * с #5633, а меню до #5667 не открывал: видимость судила кастомную роль только
 * по `extra_modules` и по совпадению её собственного кода в `allowed_roles`.
 * Практический итог был такой: API пускает, а человек до экрана не доходит.
 *
 * Это счётчик ДВЕРЕЙ. Сколько строк показать, решают права роли — прототип в
 * расчёт прав не входит (ADR-108, таблица счётчиков).
 *
 * @param {object | null | undefined} user
 * @returns {string[]}
 */
export function prototypeRoles(user) {
    const raw = user?.prototype_roles || user?.prototypeRoles;
    if (!Array.isArray(raw)) return [];
    const codes = raw
        .map(code => String(code || '').trim().toLowerCase())
        // Отсев ПОСЛЕ alias'а, а не до: `hr_manager` и `it_admin` лежат в
        // FIXED_ROLES и разворачиваются в `hr` / `it` уже внутри сверки с
        // каталогом. Проверь их по имени — и запрещённая роль проходит фильтр
        // под легаси-псевдонимом.
        .filter(code => FIXED_ROLES.has(code)
            && !PROTOTYPE_FORBIDDEN_ROLES.has(code)
            && !PROTOTYPE_FORBIDDEN_ROLES.has(ROLE_ALIASES[code] || code));
    return [...new Set(codes)];
}


// The server catalog is already intersected with purchased SKUs. Apply that
// boundary before role grants, while retaining non-removable self-service.
export function isEntitledModuleVisible(key, user, modules) {
    if (!user) return false;
    const moduleKey = (key === '' || key === '/') ? 'dashboard' : key;
    const walksAsFixed = FIXED_ROLES.has(user.role) || isCeoViewer(user) || prototypeRoles(user).length > 0;
    const selfService = walksAsFixed && MUST_HAVE_CORE.includes(moduleKey);
    if (!user.is_platform_admin && !selfService && !modules?.some(m => m.key === moduleKey)) return false;
    return isModuleVisible(key, user, modules);
}

// Only real account/position roles carry the CEO viewing privilege.
function isCeoViewer(user) {
    const positions = Array.isArray(user.position_roles) ? user.position_roles : [];
    const roles = [user.role, ...positions];
    return roles.some(raw => String(raw || '').split(',').some(role => role.trim().toLowerCase() === 'ceo'));
}

export function isModuleVisible(key, user, modules) {
    if (!user) return false;
    // (1') Departing-employee mode: уволенный с активным аккаунтом — узкий
    // whitelist даже если роль admin/owner (на случай, если сотрудник был
    // owner и его уволили — он не должен и дальше видеть всю компанию).
    if (user.linked_employee_status === 'terminated') {
        const moduleKey = (key === '' || key === '/') ? 'dashboard' : key;
        return TERMINATED_WHITELIST.includes(moduleKey);
    }
    if (user.role === 'admin' || user.is_platform_admin) return true;
    if (isCeoViewer(user)) {
        const moduleKey = (key === '' || key === '/') ? 'dashboard' : key;
        return MUST_HAVE_CORE.includes(moduleKey)
            || modules?.find(module => module.key === moduleKey)?.is_enabled !== false;
    }

    const moduleKey = (key === '' || key === '/') ? 'dashboard' : key;
    const isFixedRole = FIXED_ROLES.has(user.role);

    // Роли, КАК КОТОРЫЕ человек ходит: своя роль плюс прототипы его кастомных
    // ролей (ADR-108, #5667). Ниже этот набор читают ровно два вида шагов —
    // те, что ОТКРЫВАЮТ доступ, и те, что его СУЖАЮТ. Сужения обязаны знать
    // прототип наравне с ролью: пропусти их — и прототип `consultant` увидит
    // больше настоящего консультанта, то есть станет обходом собственного
    // ограничения. Порядок тот же, что в ADR: расширение и сужение считаются
    // по одному набору, иначе зеркальная дыра.
    const prototypes = prototypeRoles(user);
    const walksAs = [user.role, ...prototypes];
    const walksAsNormalized = walksAs.map(r => ROLE_ALIASES[r] || r);

    // `extra_modules` НЕ меняют семантику от прототипа: список, выданный
    // администратором кастомной роли, остаётся строгим whitelist'ом (шаг 3).
    // Прототип расширяет role-default, а явное сужение администратора — это
    // решение о человеке, и оно сильнее объявления роли.
    const judgedAsFixed = isFixedRole || prototypes.length > 0;

    // (1.5) HARD whitelist для модуля «Обучение и развитие» (owner decision
    // 2026-07-19, Refs #1272). Видят ТОЛЬКО роли из TRAINING_WHITELIST:
    // руководство + methodologist + hr + senior_online_manager. Обычный
    // online_manager и все прочие роли — скрыт. Жёсткий: extra_modules и каталог
    // НЕ переопределяют. admin уже вышел `true` выше (строка admin-check).
    if (moduleKey === 'training') {
        return walksAs.some(r => TRAINING_WHITELIST.has(r))
            || walksAsNormalized.some(r => TRAINING_WHITELIST.has(r));
    }

    // Consultant (внешняя роль) — строгий whitelist: только бизнес-процессы +
    // база знаний + личные self-поверхности. См. CONSULTANT_WHITELIST выше.
    if (walksAs.includes('consultant')) {
        return CONSULTANT_WHITELIST.includes(moduleKey);
    }

    if (walksAs.includes('marketing_contractor')) {
        return CONTRACTOR_WHITELIST.includes(moduleKey);
    }

    // (2) MUST_HAVE_CORE — встроенная роль либо объявленный прототип. Кастовую
    // роль без прототипа пропускаем (для неё admin рулит через extra_modules).
    // Носителю прототипа эти три поверхности личные: свой Inbox, свой профиль,
    // свой расчётный лист. Ровно на них наступил инцидент 2026-05-11.
    if (judgedAsFixed && MUST_HAVE_CORE.includes(moduleKey)) {
        return true;
    }

    // (3) Per-user extras.
    const userAssigned = user.extra_modules || user.extraModules || user.allowed_modules || [];
    const hasExtras = Array.isArray(userAssigned) && userAssigned.length > 0;
    if (!isFixedRole) {
        // Custom-роль: extras = strict whitelist. Без extras падаем в catalog.
        if (hasExtras) {
            return userAssigned.includes(moduleKey);
        }
    } else if (hasExtras && userAssigned.includes(moduleKey)) {
        // Built-in роль: extras ДОПОЛНЯЕТ role-default. Явное добавление = доступ.
        return true;
    }

    // (4) Default WHITELIST_CORE — встроенная роль либо объявленный прототип.
    if (judgedAsFixed && WHITELIST_CORE.includes(moduleKey)) {
        // online_manager — read-only: скрыт финансовый expenses. Модуль «Обучение
        // и развитие» (training) обрабатывается hard-whitelist'ом выше (owner
        // decision 2026-07-19) и сюда не доходит. dialogue-trainer остаётся
        // открыт всем built-in ролям (основной инструмент отработки диалогов).
        if (walksAs.includes('online_manager') && moduleKey === 'expenses') return false;
        return true;
    }

    // (5) Catalog lookup.
    const mod = (modules || []).find(m => m.key === moduleKey);
    if (!mod) return false;
    if (mod.is_enabled === false) return false;

    if (!judgedAsFixed) {
        // Custom-роль БЕЗ прототипа — требуется EXPLICIT match.
        if (!mod.allowed_roles || mod.allowed_roles.length === 0) return false;
        if (mod.allowed_roles.includes(user.role)) return true;
        const positionRoles = user.position_roles || [];
        for (const pr of positionRoles) {
            if (mod.allowed_roles.includes(pr)) return true;
        }
        return false;
    }

    // Fixed-role catalog (legacy): null/empty allowed_roles = open to all.
    // Носитель прототипа приходит сюда же, и это осознанно: 20 таких модулей в
    // `module_seed` — личные поверхности (inbox, cabinet, wiki, свой расчётный
    // лист, отпуска). Оставь его на строгой ветке выше — и «ходит как рекрутер»
    // означало бы разделы рекрутинга без собственного кабинета, тот самый
    // пустой сайдбар, ради которого vip_manager, consultant и
    // marketing_contractor по очереди дописывали в FIXED_ROLES.
    if (!mod.allowed_roles || mod.allowed_roles.length === 0) return true;
    if (walksAs.some(r => mod.allowed_roles.includes(r))
        || walksAsNormalized.some(r => mod.allowed_roles.includes(r))) return true;

    const positionRoles = user.position_roles || [];
    for (const pr of positionRoles) {
        const normalizedPR = ROLE_ALIASES[pr] || pr;
        if (mod.allowed_roles.includes(pr) || mod.allowed_roles.includes(normalizedPR)) return true;
    }

    return false;
}
